import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { REGIONS } from '@leanin/shared/region';
import type { Region } from '@leanin/shared/region';
import { useMember } from '@/lib/use-session';
import { useOnboardingVisitPing } from '@/lib/onboarding/useVisitPing';
import { NetworksChrome } from '@/components/networks/NetworksChrome';
import { GroupsListBody } from '@/components/group/index';
import { groupSummaryToListItem } from '@/components/networks/adapters';
import type { NetworkListItem } from '@/components/networks/adapters';
import {
  groupKeys,
  type ListNetworksArgs,
  type NetworkScope,
} from '@/lib/queries/groups';

const NETWORK_SCOPES: readonly NetworkScope[] = ['all', 'mine'];

type NetworksSearch = {
  q?: string;
  scope?: NetworkScope;
  region?: ReadonlyArray<Region>;
};

function parseScope(v: unknown): NetworkScope | undefined {
  return typeof v === 'string' &&
    (NETWORK_SCOPES as readonly string[]).includes(v)
    ? (v as NetworkScope)
    : undefined;
}

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function parseRegions(v: unknown): ReadonlyArray<Region> | undefined {
  const raw =
    typeof v === 'string'
      ? v.split(',')
      : Array.isArray(v)
        ? v.flatMap((x) => (typeof x === 'string' ? x.split(',') : []))
        : null;
  if (raw === null) return undefined;
  const valid = (REGIONS as readonly string[])
    .filter((r) => raw.includes(r))
    .map((r) => r as Region);
  return valid.length > 0 ? valid : undefined;
}

const PAGE_SIZE = 24;

export const Route = createFileRoute('/_public/networks/')({
  validateSearch: (raw: Record<string, unknown>): NetworksSearch => {
    const q = pickStr(raw.q);
    const scope = parseScope(raw.scope);
    const region = parseRegions(raw.region);
    return {
      ...(q !== undefined ? { q } : {}),
      ...(scope !== undefined ? { scope } : {}),
      ...(region !== undefined ? { region } : {}),
    };
  },
  component: NetworksListPage,
});

function NetworksListPage() {
  const search = Route.useSearch();
  const member = useMember();
  const signedIn = member !== null;
  useOnboardingVisitPing('visit_hub', signedIn);

  // Anonymous viewers cannot resolve viewer-bound scopes ('mine' has no
  // meaning without a viewer). Force 'all' regardless of URL state — the
  // chrome strips the My Networks tab and start-a-network CTA in parallel.
  const scope = signedIn ? (search.scope ?? 'mine') : 'all';
  const q = search.q?.trim() ?? '';
  const region = search.region ?? [];

  const listArgs: ListNetworksArgs = {
    pageSize: PAGE_SIZE,
    ...(q.length > 0 ? { q } : {}),
    ...(scope !== 'all' ? { scope } : {}),
    ...(region.length > 0 ? { region: region.join(',') } : {}),
  };

  // Full mine list — drives the section split (Leading / Joined / Archived)
  // under My Networks, and the "Yours" tab count in the chrome. Disabled
  // when no session: the endpoint requires auth and the data is irrelevant
  // to the anon directory view.
  const myNetworksQuery = useQuery({
    ...groupKeys.network.mine({ includeArchived: true }),
    enabled: signedIn,
  });
  const mine: readonly NetworkListItem[] | null = myNetworksQuery.data
    ? myNetworksQuery.data.items.map(groupSummaryToListItem)
    : null;

  return (
    <NetworksChrome signedIn={signedIn}>
      <GroupsListBody
        kind="network"
        listArgs={listArgs}
        mine={mine}
        isError={myNetworksQuery.isError}
        signedIn={signedIn}
      />
    </NetworksChrome>
  );
}
